/**
 * Error Boundary Component
 * Catches React component errors and displays a user-friendly message
 */

import React from "react";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import safeToast from "utils/safeToast";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Check if this is a toast-related error
    const errorMessage = error?.message || error?.toString() || "";
    const isToastError = 
      errorMessage.includes("removalReason") ||
      errorMessage.includes("Cannot set properties of undefined");

    // Don't show error UI for toast errors
    if (isToastError) {
      return { hasError: false };
    }

    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Check if this is a toast-related error that should be suppressed
    const errorMessage = error?.message || error?.toString() || "";
    const errorStack = errorInfo?.componentStack || error?.stack || "";
    
    const isToastError = 
      errorMessage.includes("removalReason") ||
      errorMessage.includes("Cannot set properties of undefined") ||
      errorStack.includes("closeToast") ||
      errorStack.includes("removalReason");

    // Suppress toast errors silently
    if (isToastError) {
      if (process.env.NODE_ENV === "development") {
        console.warn("Toast error suppressed in ErrorBoundary:", errorMessage);
      }
      // Don't set error state for toast errors - just reset
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
      });
      return;
    }

    // Log error details for non-toast errors
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // Show user-friendly error message
    try {
      safeToast.error("An unexpected error occurred. Please try refreshing the page.");
    } catch (toastError) {
      console.error("Failed to show error toast:", toastError);
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      return (
        <MDBox
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          p={3}
          sx={{
            backgroundColor: "background.default",
          }}
        >
          <MDBox
            maxWidth="600px"
            width="100%"
            p={4}
            borderRadius="lg"
            sx={{
              backgroundColor: "background.paper",
              boxShadow: 3,
            }}
          >
            <MDTypography variant="h4" color="error" fontWeight="bold" mb={2}>
              Oops! Something went wrong
            </MDTypography>
            
            <MDTypography variant="body1" color="text" mb={3}>
              An unresolved problem was found. We apologize for the inconvenience.
              Please try refreshing the page or contact support if the problem persists.
            </MDTypography>

            {process.env.NODE_ENV === "development" && this.state.error && (
              <MDBox
                mb={3}
                p={2}
                borderRadius="md"
                sx={{
                  backgroundColor: "grey.100",
                  maxHeight: "200px",
                  overflow: "auto",
                }}
              >
                <MDTypography variant="caption" color="error" component="pre">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </MDTypography>
              </MDBox>
            )}

            <MDBox display="flex" gap={2} flexWrap="wrap">
              <MDButton
                variant="gradient"
                color="info"
                onClick={this.handleReload}
              >
                Reload Page
              </MDButton>
              <MDButton
                variant="outlined"
                color="secondary"
                onClick={this.handleReset}
              >
                Try Again
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ErrorBoundary;

